import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { organizationService } from '../api';
import { Button, ThemedText, ActionMenu, ErrorState } from '../components';
import { DemoStatsSection } from '../components/DemoStatsSection';
import { ManageAvatarPoolModal } from '../components/ManageAvatarPoolModal';
import { extractItems } from '../utils/responseUtils';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { StatusBadge } from '../components/Badge';
import { ExternalLink, List, BarChart3, Eye, LogIn, Users, Wand2, Pencil, Link2, Plus } from 'lucide-react';
import { parseDemoLanding, API_BASE_URL, demoPublicUrl, demoLaunchUrl, demoBuilderEmail } from '../utils/demoStudio';

const SubTabBar = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SubTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${({ $active, theme }) => ($active ? theme.colors.background : 'transparent')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ $active, theme }) => ($active ? theme.colors.text : theme.colors.muted)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${({ $active, theme }) => ($active ? (theme.shadows?.sm || '0 1px 2px rgba(35,37,98,0.08)') : 'none')};
  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;

// ── Demo card ──
const CardGrid = styled.div`
  display: grid;
  gap: ${p => p.theme.spacing.md};
`;

const DemoCard = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: ${p => p.theme.colors.tint};
    box-shadow: ${p => p.theme.shadows?.sm || '0 2px 8px rgba(35,37,98,0.06)'};
    transform: translateY(-1px);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${p => p.theme.spacing.md};
`;

const TitleWrap = styled.div`
  min-width: 0;
`;

const DemoName = styled.button`
  display: inline-block;
  padding: 0;
  border: none;
  background: none;
  color: ${p => p.theme.colors.text};
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  line-height: 1.2;
  &:hover { color: ${p => p.theme.colors.tint}; }
`;

const Headline = styled.div`
  color: ${p => p.theme.colors.muted};
  font-size: ${p => p.theme.typography.sizes.small};
  margin-top: 2px;
`;

const ActivityChips = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  flex-shrink: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: ${p => p.theme.typography.sizes.small};
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  svg { color: ${p => p.theme.colors.tint}; }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.lg};
  margin-top: ${p => p.theme.spacing.md};
  padding-top: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const MetaLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
`;

const MetaValue = styled.span`
  font-size: ${p => p.theme.typography.sizes.small};
  color: ${p => p.theme.colors.text};
  word-break: break-all;
`;

const PublicLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${p => p.theme.spacing.xs};
  color: ${p => p.theme.colors.tint};
  font-size: ${p => p.theme.typography.sizes.small};
  word-break: break-all;
  &:hover { text-decoration: underline; }
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-top: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xxl} ${p => p.theme.spacing.xl};
  border: 1px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  color: ${p => p.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xxl};
  color: ${p => p.theme.colors.muted};
`;

const FootNote = styled.div`
  color: ${p => p.theme.colors.muted};
  font-size: ${p => p.theme.typography.sizes.small};
  margin-top: ${p => p.theme.spacing.lg};
  code {
    background: ${p => p.theme.colors.backgroundSecondary};
    border: 1px solid ${p => p.theme.colors.border};
    border-radius: ${p => p.theme.borderRadius.sm};
    padding: 1px 6px;
    font-size: 12px;
  }
`;

const DemoStudioPage = () => {
  const theme = useTheme();
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [demos, setDemos] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isAvatarPoolOpen, setAvatarPoolOpen] = useState(false);

  const tab = searchParams.get('tab') === 'stats' ? 'stats' : 'demos';
  const setTab = (key) => setSearchParams({ tab: key });

  useEffect(() => {
    fetchDemos();
    fetchSummaries();
  }, []);

  const fetchDemos = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const response = await organizationService.getOrganizations({ includeInactive: true });
      setDemos(extractItems(response).filter(org => org.isDemo));
    } catch (error) {
      console.error('Failed to fetch demos:', error);
      setFetchError(error?.message || 'Failed to load demos. Please check your connection and try again.');
      toast.error('Failed to load demos');
    } finally {
      setLoading(false);
    }
  };

  // Per-demo view/login counts for the card chips. Secondary data — fail soft (chips render '—').
  const fetchSummaries = async () => {
    try {
      const data = await organizationService.getDemoStatsSummary();
      setSummaries(Array.isArray(data) ? data : extractItems(data));
    } catch (error) {
      console.error('Failed to fetch demo stats summary:', error);
    }
  };

  const summaryFor = (orgId) => summaries.find(s => s.organizationId === orgId);

  const copy = (text, label = 'Copied!') => {
    if (!text) { toast.info('Nothing to copy'); return; }
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const togglePublished = async (org) => {
    const demo = parseDemoLanding(org.demoLandingJson);
    try {
      await organizationService.updateOrganization(org.id, {
        isDemo: true,
        demoSlug: org.demoSlug || '',
        demoLandingJson: JSON.stringify({ ...demo, published: !demo.published }),
      });
      toast.success(demo.published ? 'Demo unpublished' : 'Demo published');
      fetchDemos();
    } catch (error) {
      console.error('Failed to toggle publish state:', error);
      toast.error(error?.message || 'Failed to update demo');
    }
  };

  const deleteDemo = async (org) => {
    if (!window.confirm('Delete this demo and all its data? This cannot be undone.')) return;
    try {
      await organizationService.deleteDemo(org.id);
      toast.success('Demo deleted');
      fetchDemos();
    } catch (error) {
      console.error('Failed to delete demo:', error);
      toast.error(error?.message || 'Failed to delete demo');
    }
  };

  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/demo-studio">
        <PageContainer theme={theme}>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  const renderDemoCard = (org) => {
    const demo = parseDemoLanding(org.demoLandingJson);
    const publicUrl = demoPublicUrl(org.demoSlug);
    const launchUrl = demoLaunchUrl(org.demoSlug);
    const summary = summaryFor(org.id);
    const builderEmail = org.demoSlug ? demoBuilderEmail(org.demoSlug) : '—';

    return (
      <DemoCard key={org.id} theme={theme}>
        <CardTop theme={theme}>
          <TitleWrap>
            <DemoName theme={theme} onClick={() => navigate(`/demo-studio/${org.id}/page`)}>
              {org.name}
            </DemoName>
            {demo.headline && <Headline theme={theme}>{demo.headline}</Headline>}
          </TitleWrap>
          <ActivityChips theme={theme}>
            <StatusBadge theme={theme} isActive={!!demo.published}>
              {demo.published ? 'Live' : 'Draft'}
            </StatusBadge>
            {summary && (
              <>
                <Chip theme={theme} title="Views"><Eye size={14} /> {summary.totalViews}</Chip>
                <Chip theme={theme} title="Viewer logins"><LogIn size={14} /> {summary.totalLogins}</Chip>
              </>
            )}
          </ActivityChips>
        </CardTop>

        <MetaRow theme={theme}>
          <MetaItem theme={theme}>
            <MetaLabel theme={theme}>Slug</MetaLabel>
            <MetaValue theme={theme}>{org.demoSlug || '—'}</MetaValue>
          </MetaItem>
          <MetaItem theme={theme}>
            <MetaLabel theme={theme}>Public page</MetaLabel>
            {org.demoSlug ? (
              <PublicLink theme={theme} href={publicUrl} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}>
                <ExternalLink size={13} /> {publicUrl.replace(/^https?:\/\//, '')}
              </PublicLink>
            ) : (
              <MetaValue theme={theme} style={{ color: theme.colors.muted }}>No slug</MetaValue>
            )}
          </MetaItem>
          <MetaItem theme={theme}>
            <MetaLabel theme={theme}>Viewer (public)</MetaLabel>
            <MetaValue theme={theme}>{demo.demoLoginEmail || '—'}</MetaValue>
          </MetaItem>
          <MetaItem theme={theme}>
            <MetaLabel theme={theme}>Builder (internal)</MetaLabel>
            <MetaValue theme={theme}>{builderEmail}</MetaValue>
          </MetaItem>
        </MetaRow>

        <CardActions theme={theme}>
          <Button onClick={() => navigate(`/demo-studio/${org.id}/page`)}>
            <Wand2 size={14} /> Design page
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/demo-studio/${org.id}`)}>
            <Pencil size={14} /> Edit
          </Button>
          {org.demoSlug && (
            <Button variant="secondary" onClick={() => window.open(publicUrl, '_blank', 'noopener')}>
              <ExternalLink size={14} /> Open
            </Button>
          )}
          <ActionMenu actions={[
            { label: 'Copy public link', onClick: () => copy(publicUrl, 'Public link copied') },
            { label: 'Copy launch link', onClick: () => copy(launchUrl, 'Launch link copied') },
            { label: 'Copy viewer email', onClick: () => copy(demo.demoLoginEmail || '', 'Viewer email copied') },
            { label: 'Copy builder email', onClick: () => copy(builderEmail, 'Builder email copied') },
            { label: 'View stats', onClick: () => setSearchParams({ tab: 'stats', org: String(org.id) }) },
            { label: demo.published ? 'Unpublish' : 'Publish', onClick: () => togglePublished(org) },
            { label: 'Delete demo', variant: 'danger', onClick: () => deleteDemo(org) },
          ]} />
        </CardActions>
      </DemoCard>
    );
  };

  return (
    <Layout currentPath="/demo-studio">
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <div>
            <ThemedText type="title">Demo Studio</ThemedText>
            <ThemedText type="subtitle">Create, design, and track your public demo experiences</ThemedText>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => setAvatarPoolOpen(true)}
              title="View, upload, generate, and remove the shared demo avatars">
              <Users size={14} /> Avatar pool
            </Button>
            <Button onClick={() => navigate('/demo-studio/new')}>
              <Plus size={14} /> New demo
            </Button>
          </div>
        </PageHeader>

        <SubTabBar theme={theme}>
          <SubTab theme={theme} $active={tab === 'demos'} onClick={() => setTab('demos')}>
            <List size={14} /> Demos
          </SubTab>
          <SubTab theme={theme} $active={tab === 'stats'} onClick={() => setTab('stats')}>
            <BarChart3 size={14} /> Statistics
          </SubTab>
        </SubTabBar>

        {tab === 'stats' ? (
          <DemoStatsSection demos={demos} />
        ) : loading ? (
          <LoadingState theme={theme}><ThemedText>Loading demos…</ThemedText></LoadingState>
        ) : fetchError ? (
          <ErrorState theme={theme} title="Failed to load demos" message={fetchError} onRetry={fetchDemos} />
        ) : demos.length === 0 ? (
          <EmptyState theme={theme}>
            <Link2 size={28} style={{ color: theme.colors.muted, marginBottom: theme.spacing.sm }} />
            <ThemedText type="defaultSemiBold" style={{ display: 'block' }}>No demos yet</ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted, display: 'block', marginTop: theme.spacing.xs, marginBottom: theme.spacing.lg }}>
              Create your first demo organization to share a public, ready-to-explore aQRate experience.
            </ThemedText>
            <Button onClick={() => navigate('/demo-studio/new')}><Plus size={14} /> New demo</Button>
          </EmptyState>
        ) : (
          <>
            <CardGrid theme={theme}>
              {demos.map(renderDemoCard)}
            </CardGrid>
            <FootNote theme={theme}>
              Public demo pages render at <code>{API_BASE_URL}/demos/&lt;slug&gt;</code>. Use <strong>Design page</strong> to author each one with AI.
            </FootNote>
          </>
        )}
      </PageContainer>
      <ManageAvatarPoolModal isOpen={isAvatarPoolOpen} onClose={() => setAvatarPoolOpen(false)} />
    </Layout>
  );
};

export default DemoStudioPage;
export { DemoStudioPage };
